import { useState, useRef, useEffect } from 'react';
import { GoogleMap, Marker, Autocomplete, DirectionsRenderer, useLoadScript } from '@react-google-maps/api';
import { URL_CONFIG } from '@/configs/environment';
import './style.scss';
import { Coordinate } from '@/types/location';
import classNames from 'classnames';
import { convertDistance, deleteJapanText } from '@/utils/utils';
import { useTranslation } from 'react-i18next';
import AnimateButton from '@/components/general/button-custom/AnimateButton';
import MapPinCustom from '@/components/icon/MapPinCustom';
import { LocationIcon } from '@/components/icon';
import { ArrowRightIconCus } from '@/components/icon/ArrowRightIconCus';
import iconPinMap from '@/assets/images/icon-location-pin-map.png';
import iconPinMapKickbaord from '@/assets/images/icon-location-pin-map-kickboard.png';
import { useLocation } from 'react-router-dom';
import { isPathEk } from '@/utils/routes';

const containerStyle = {
    width: '100%',
    height: '500px'
};
type Library = 'places' | 'drawing' | 'geometry' | 'visualization';
const libraries: Library[] = ['places'];
const defaultCenter = { lat: 35.6895, lng: 139.6917 };

interface MapWithSearchProps {
    fbDirection?: Function,
    showDescription: Function,
    className?: string
}
export default function MapWithSearch({ fbDirection, showDescription, className }: MapWithSearchProps) {
    // hook
    const { t, i18n: { language } } = useTranslation();
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: `${URL_CONFIG.GOOGLE_MAP.private}`,
        libraries,
        id: 'google-map-script',
        language: language
    });
    const { pathname } = useLocation();
    const isEk = isPathEk(pathname);

    // state
    const [origin, setOrigin] = useState<Coordinate | null>(defaultCenter);
    const [originText, setOriginText] = useState<string>('');
    const [destination, setDestination] = useState<Coordinate | null>(null); // Vị trí mặc định cho điểm đến
    const [destinationText, setDestinationText] = useState<string>('');
    const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);
    const [distance, setDistance] = useState<string>('0km');
    const [showGoogleMap, setShowGGMap] = useState<boolean>(false);

    const mapRef = useRef<google.maps.Map | null>(null);
    const originRef = useRef<google.maps.places.Autocomplete | null>(null);
    const destinationRef = useRef<google.maps.places.Autocomplete | null>(null);

    // lifecycle
    useEffect(() => {
        if (window.google && isLoaded && origin && destination) {
            calculateRoute();
        }
    }, [origin, destination, isLoaded]);

    // function
    // Hàm tính toán đường đi
    const calculateRoute = () => {
        if (!window.google || !origin || !destination) return;
        const directionsService = new google.maps.DirectionsService();

        origin && destination && directionsService.route(
            {
                origin,
                destination,
                travelMode: google.maps.TravelMode.DRIVING // Phương tiện công cộng (bus, tàu điện, ...)
            },
            (result, status) => {
                if (status === google.maps.DirectionsStatus.OK && result) {
                    setDirections(result);
                    let distanceVal = result.routes[0].legs[0].distance?.value || 0;
                    setDistance(convertDistance(distanceVal * 2));
                    fbDirection && fbDirection(result);
                }
            }
        );
    };

    // Xử lý khi chọn địa điểm từ ô tìm kiếm
    const handlePlaceSelect = (type: 'origin' | 'destination') => {
        const autocomplete =
            type === 'origin' ? originRef.current : destinationRef.current;
        const place = autocomplete?.getPlace();
        if (place?.geometry?.location) {
            const newCoords = {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng()
            };

            const tmpAddress = deleteJapanText(place.formatted_address || place.name || '');

            if (type === 'origin') {
                setOrigin(newCoords);
                // Cập nhật text, ưu tiên formatted_address nếu có, nếu không lấy name
                setOriginText(tmpAddress);
            } else {
                setDestination(newCoords);
                setDestinationText(tmpAddress);
            }
        }
    };

    if (!isLoaded) return <div>Loading...</div>;

    return (
        <div className={classNames('w-full', className)}>
            <div className='flex point-900:flex-row flex-col gap-4 justify-center items-center'>
                <AnimateButton
                    contentComp={(
                        <p className='flex items-center gap-5 text-fs-mid18 leading-[27px]'>
                            <MapPinCustom className='size-5' strokeWidth={1.5} />
                            <p className=''>{t('button.google_map')}</p>
                        </p>
                    )}
                    type='button'
                    className='!py-[min(20px,5vw)]'
                    parentClassName='xsm:w-fit w-full'
                    onClick={() => {
                        setShowGGMap(!showGoogleMap);
                    }}
                >
                </AnimateButton>
                <div className='h-full max-h-12 flex items-center'>
                    <ArrowRightIconCus className='transform rotate-90 point-900:rotate-0 ' />
                </div>
                <div className='py-[18px] px-[40px] xsm:w-[370px] w-full font-bold flex items-center bg-subtle h-full rounded-[5px]'>
                    <p className='w-[100px]'>距離目安</p>
                    <p className='text-customRed'>
                        {distance}
                        {' '}
                        (往復)
                    </p>

                </div>
            </div>

            <div className={classNames(!showGoogleMap && 'hidden')}>
                {/* Ô nhập địa điểm */}
                <div className={classNames('flex gap-5 mb-3 w-full mt-4 xsm:flex-row flex-col items-center')}>
                    <Autocomplete
                        onLoad={autocomplete => (originRef.current = autocomplete)}
                        onPlaceChanged={() => handlePlaceSelect('origin')}
                        className='xsm:w-fit w-full'
                    >
                        <div className='relative w-full'>
                            <LocationIcon className='size-4 absolute top-3 left-5' />
                            <input
                                className='auto-complte-input '
                                value={originText}
                                onChange={e => setOriginText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault(); // Ngăn submit khi nhấn Enter
                                    }
                                }}
                                type='text'
                                placeholder={t('placeholder.departure_point')}
                            />
                        </div>

                    </Autocomplete>
                    <div className='flex items-center'>
                        <ArrowRightIconCus className='transform rotate-90 xsm:rotate-0 ' />
                    </div>
                    <Autocomplete
                        onLoad={autocomplete => (destinationRef.current = autocomplete)}
                        onPlaceChanged={() => handlePlaceSelect('destination')}
                        className='xsm:w-fit w-full'
                    >
                        <div className='relative w-full'>
                            <LocationIcon className='size-4 absolute top-3 left-5' />
                            <input
                                className='auto-complte-input '
                                value={destinationText}
                                onChange={e => setDestinationText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault(); // Ngăn submit khi nhấn Enter
                                    }
                                }}
                                type='text'
                                placeholder={t('placeholder.destination_point')}
                            />
                        </div>
                    </Autocomplete>
                </div>

                {/* Bản đồ */}
                <GoogleMap
                    mapContainerStyle={containerStyle}
                    center={origin || defaultCenter}
                    zoom={12}
                    onLoad={(map) => {
                        (mapRef.current = map);
                    }}
                    options={{
                        mapTypeControl: false,
                        streetViewControl: false,
                        fullscreenControl: false,
                        mapTypeId: window.google ? window.google.maps.MapTypeId.ROADMAP : 'roadmap'
                    }}
                >
                    {origin && (
                        <Marker
                            position={origin}
                            icon={{
                                url: isEk ? iconPinMapKickbaord : iconPinMap
                            }}
                        />
                    )}

                    {/* Điểm đến - Marker đỏ */}
                    {destination && (
                        <Marker
                            position={destination}
                            icon={{
                                url: isEk ? iconPinMapKickbaord : iconPinMap
                            }}
                        />
                    )}

                    {/* Vẽ đường đi */}
                    {directions && (
                        <DirectionsRenderer
                            directions={directions}
                            options={{
                                suppressMarkers: true, // ❌ Ẩn marker mặc định của Google
                                polylineOptions: {
                                    strokeColor: '#1A73E8', // 🎨 Màu xanh dương đậm của Google Maps
                                    strokeWeight: 6,
                                    strokeOpacity: 1
                                }
                            }}
                        />
                    )}
                </GoogleMap>
            </div>

        </div>
    );
};
